import { useTranslation } from 'react-i18next';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useIsAccountAdmin } from '../../hooks/useIsAccountAdmin';
import { useNavOverflow } from '../../hooks/useNavOverflow';
import ROUTES, { LOCATION_SEGMENTS, locationPath } from '../../routes/routeConstants';
import { HeaderNavGhost } from './HeaderNavGhost';
import { HeaderNavItem, type NavItem } from './HeaderNavItem';
import { HeaderNavMore } from './HeaderNavMore';
import styles from './HeaderNav.module.css';

// Persistent top navigation (replaces the old hamburger side drawer). Every
// page the user can reach is listed, and the list folds into "More" from the
// end only when the bar runs out of room.
export const HeaderNav = () => {
  const { t } = useTranslation();
  const { activeLocationId } = useActiveLocation();
  const isAccountAdmin = useIsAccountAdmin();

  const atLocation = (...segments: string[]): string | null =>
    activeLocationId ? locationPath(activeLocationId, ...segments) : null;

  // Ordered by how often the page is opened during a planning day, so the
  // least used ones are the first to fold away.
  const allItems: NavItem[] = [
    { labelKey: 'nav-home', to: `/${ROUTES.locations}`, isExactMatch: true },
    { labelKey: 'nav-year-overview', to: atLocation(LOCATION_SEGMENTS.yearOverview) },
    { labelKey: 'nav-week-overview', to: atLocation(LOCATION_SEGMENTS.weekOverview) },
    { labelKey: 'nav-patients', to: atLocation(LOCATION_SEGMENTS.patients) },
    { labelKey: 'nav-economics', to: atLocation(LOCATION_SEGMENTS.economics) },
    { labelKey: 'nav-bookings', to: atLocation(ROUTES.bookings), isAdminOnly: true },
    { labelKey: 'nav-treatment-plan-builder', to: `/${ROUTES.configuration}`, isAdminOnly: true }
  ];

  // Pages the current user has no access to are removed entirely, not
  // disabled: a page they can never reach is noise, not information.
  const items = allItems.filter((item) => !item.isAdminOnly || isAccountAdmin);

  const { navRef, ghostRef, visibleCount } = useNavOverflow(items.length);

  const barItems = items.slice(0, visibleCount);
  const foldedItems = items.slice(visibleCount);

  return (
    <nav ref={navRef} className={styles.nav} aria-label={t('navigation')}>
      <HeaderNavGhost items={items} listRef={ghostRef} />
      <ul className={styles.navList}>
        {barItems.map((item) => (
          <HeaderNavItem key={item.labelKey} item={item} />
        ))}
        {foldedItems.length > 0 && (
          <li>
            <HeaderNavMore items={foldedItems} />
          </li>
        )}
      </ul>
    </nav>
  );
};
